/**
 * ResponsiveModal Component
 *
 * A responsive modal that renders as a Dialog on desktop and a Drawer (bottom sheet) on mobile.
 * Uses the same API as Dialog for easy adoption.
 */

import * as React from 'react'
import { useMediaQuery } from '@/hooks/use-media-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer'
import { cn } from '@/lib/utils'

interface ResponsiveModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

interface ResponsiveModalContentProps {
  className?: string
  children: React.ReactNode
}

interface ResponsiveModalHeaderProps {
  className?: string
  children: React.ReactNode
}

interface ResponsiveModalFooterProps {
  className?: string
  children: React.ReactNode
}

interface ResponsiveModalTitleProps {
  className?: string
  children: React.ReactNode
}

interface ResponsiveModalDescriptionProps {
  className?: string
  children: React.ReactNode
}

const ResponsiveModalContext = React.createContext<{ isDesktop: boolean }>({
  isDesktop: true,
})

function ResponsiveModal({ open, onOpenChange, children }: ResponsiveModalProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)')

  if (isDesktop) {
    return (
      <ResponsiveModalContext.Provider value={{ isDesktop }}>
        <Dialog open={open} onOpenChange={onOpenChange}>
          {children}
        </Dialog>
      </ResponsiveModalContext.Provider>
    )
  }

  return (
    <ResponsiveModalContext.Provider value={{ isDesktop }}>
      <Drawer open={open} onOpenChange={onOpenChange}>
        {children}
      </Drawer>
    </ResponsiveModalContext.Provider>
  )
}

function ResponsiveModalContent({ className, children }: ResponsiveModalContentProps) {
  const { isDesktop } = React.useContext(ResponsiveModalContext)

  if (isDesktop) {
    return <DialogContent className={className}>{children}</DialogContent>
  }

  return (
    <DrawerContent className={cn('px-4 pb-6', className)}>
      {children}
    </DrawerContent>
  )
}

function ResponsiveModalHeader({ className, children }: ResponsiveModalHeaderProps) {
  const { isDesktop } = React.useContext(ResponsiveModalContext)

  if (isDesktop) {
    return <DialogHeader className={className}>{children}</DialogHeader>
  }

  // On mobile, use left-aligned text and proper padding (px handled by parent)
  return <DrawerHeader className={cn('px-0 pt-2 text-left', className)}>{children}</DrawerHeader>
}

function ResponsiveModalFooter({ className, children }: ResponsiveModalFooterProps) {
  const { isDesktop } = React.useContext(ResponsiveModalContext)

  if (isDesktop) {
    return <DialogFooter className={className}>{children}</DialogFooter>
  }

  return <DrawerFooter className={className}>{children}</DrawerFooter>
}

function ResponsiveModalTitle({ className, children }: ResponsiveModalTitleProps) {
  const { isDesktop } = React.useContext(ResponsiveModalContext)

  if (isDesktop) {
    return <DialogTitle className={className}>{children}</DialogTitle>
  }

  return <DrawerTitle className={className}>{children}</DrawerTitle>
}

function ResponsiveModalDescription({ className, children }: ResponsiveModalDescriptionProps) {
  const { isDesktop } = React.useContext(ResponsiveModalContext)

  if (isDesktop) {
    return <DialogDescription className={className}>{children}</DialogDescription>
  }

  return <DrawerDescription className={className}>{children}</DrawerDescription>
}

function ResponsiveModalClose({ className, children }: { className?: string; children?: React.ReactNode }) {
  const { isDesktop } = React.useContext(ResponsiveModalContext)

  if (isDesktop) {
    return <DialogClose className={className}>{children}</DialogClose>
  }

  return <DrawerClose className={className}>{children}</DrawerClose>
}

export {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalClose,
}
